import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiAward, FiX } from 'react-icons/fi';
import './HR.css';

const STATUS_CFG = {
  planned:     { label:'Planned',     bg:'#f1f5f9', color:'#475569' },
  in_progress: { label:'In Progress', bg:'#dbeafe', color:'#1e40af' },
  completed:   { label:'Completed',   bg:'#dcfce7', color:'#14532d' },
  expired:     { label:'Expired',     bg:'#fee2e2', color:'#991b1b' },
};

const EMPTY = { userId:'', title:'', category:'', provider:'', description:'', startDate:'', endDate:'', status:'planned', score:'', certificateUrl:'', expiryDate:'', isCertified:false, notes:'' };

export default function Training() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const [trainings, setTrainings] = useState([]);
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(null); // null | 'add' | training obj
  const [form,      setForm]      = useState(EMPTY);
  const [filterUser, setFilterUser] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    load();
    if (isAdmin) api.get('/users').then(r => setMembers((r.data.users||[]).filter(u=>u.role!=='client')));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterUser) params.userId = filterUser;
      const r = await api.get('/training', { params });
      setTrainings(r.data.trainings || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterUser]);

  const openAdd  = () => { setForm({ ...EMPTY, userId: isAdmin ? '' : String(user.id) }); setModal('add'); };
  const openEdit = (t) => { setForm({ ...t, userId: String(t.userId) }); setModal(t); };

  const save = async () => {
    if (!form.title.trim() || !form.userId) return toast.error('Employee and title required');
    try {
      if (modal === 'add') { const r = await api.post('/training', form); setTrainings(prev=>[r.data.training,...prev]); toast.success('Added'); }
      else { const r = await api.put(`/training/${modal.id}`, form); setTrainings(prev=>prev.map(t=>t.id===modal.id?r.data.training:t)); toast.success('Updated'); }
      setModal(null);
    } catch { toast.error('Failed to save'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this training record?')) return;
    try { await api.delete(`/training/${id}`); setTrainings(prev=>prev.filter(t=>t.id!==id)); }
    catch { toast.error('Failed'); }
  };

  const filtered = filterStatus ? trainings.filter(t=>t.status===filterStatus) : trainings;

  const INP = { width:'100%', padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' };

  return (
    <div className="hr-page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0 }}>Training & Certifications</h1>
          <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>Manage employee training programs and certifications</p>
        </div>
        {isAdmin && <button onClick={openAdd} style={{ padding:'10px 20px', background:'var(--pm-gradient)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}><FiPlus/> Add Training</button>}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap' }}>
        {isAdmin && (
          <select style={{ padding:'8px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', background:'#fff' }}
            value={filterUser} onChange={e=>setFilterUser(e.target.value)}>
            <option value="">All Employees</option>
            {members.map(m=><option key={m._id||m.id} value={m._id||m.id}>{m.name}</option>)}
          </select>
        )}
        <select style={{ padding:'8px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', background:'#fff' }}
          value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CFG).map(([v,c])=><option key={v} value={v}>{c.label}</option>)}
        </select>
      </div>

      {/* Cards */}
      {loading ? <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>Loading…</div>
      : filtered.length === 0 ? <div className="card" style={{ padding:60, textAlign:'center', color:'#94a3b8' }}><FiAward size={40} style={{ marginBottom:12, display:'block', margin:'0 auto 12px' }}/>No training records found</div>
      : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
          {filtered.map(t => {
            const sc = STATUS_CFG[t.status] || STATUS_CFG.planned;
            return (
              <div key={t.id} className="card" style={{ padding:20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:'#0f172a', marginBottom:4 }}>{t.title}</div>
                    {t.employee && <div style={{ fontSize:12, color:'#64748b' }}>👤 {t.employee.name}</div>}
                    {t.provider && <div style={{ fontSize:12, color:'#94a3b8' }}>🏫 {t.provider}</div>}
                  </div>
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    {isAdmin && <button onClick={()=>openEdit(t)} style={{ background:'#f1f5f9', border:'none', borderRadius:7, padding:'5px 9px', cursor:'pointer', color:'#475569' }}><FiEdit2 size={13}/></button>}
                    {isAdmin && <button onClick={()=>del(t.id)} style={{ background:'#fee2e2', border:'none', borderRadius:7, padding:'5px 9px', cursor:'pointer', color:'#ef4444' }}><FiTrash2 size={13}/></button>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:10 }}>
                  <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:sc.bg, color:sc.color }}>{sc.label}</span>
                  {t.isCertified && <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:'#fef9c3', color:'#854d0e' }}>🏆 Certified</span>}
                  {t.category && <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, background:'#f1f5f9', color:'#64748b' }}>{t.category}</span>}
                </div>
                <div style={{ fontSize:12, color:'#94a3b8', display:'flex', gap:12, flexWrap:'wrap' }}>
                  {t.startDate && <span>📅 {new Date(t.startDate).toLocaleDateString()}{t.endDate ? ` → ${new Date(t.endDate).toLocaleDateString()}` : ''}</span>}
                  {t.score != null && t.score !== '' && <span>📊 Score: {t.score}%</span>}
                  {t.expiryDate && <span>⏳ Expires: {new Date(t.expiryDate).toLocaleDateString()}</span>}
                </div>
                {t.certificateUrl && <a href={t.certificateUrl} target="_blank" rel="noopener noreferrer" style={{ display:'inline-block', marginTop:8, fontSize:12, color:'#667eea', fontWeight:600 }}>🔗 View Certificate</a>}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
          onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, width:560, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px #0003' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800 }}>{modal==='add' ? 'Add Training' : 'Edit Training'}</h3>
              <button onClick={()=>setModal(null)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}><FiX/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {isAdmin && (
                <label style={{ gridColumn:'1/-1', fontSize:11, fontWeight:700, color:'#64748b' }}>Employee *
                  <select style={{ ...INP, marginTop:4 }} value={form.userId} onChange={e=>setForm(f=>({...f,userId:e.target.value}))}>
                    <option value="">Select employee</option>
                    {members.map(m=><option key={m._id||m.id} value={m._id||m.id}>{m.name}</option>)}
                  </select>
                </label>
              )}
              <label style={{ gridColumn:'1/-1', fontSize:11, fontWeight:700, color:'#64748b' }}>Training Title *
                <input style={{ ...INP, marginTop:4 }} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. AWS Cloud Practitioner"/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Category
                <input style={{ ...INP, marginTop:4 }} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} placeholder="e.g. Cloud, Security"/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Provider / Platform
                <input style={{ ...INP, marginTop:4 }} value={form.provider} onChange={e=>setForm(f=>({...f,provider:e.target.value}))} placeholder="e.g. Coursera, Udemy"/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Status
                <select style={{ ...INP, marginTop:4 }} value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                  {Object.entries(STATUS_CFG).map(([v,c])=><option key={v} value={v}>{c.label}</option>)}
                </select>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Score (%)
                <input type="number" style={{ ...INP, marginTop:4 }} value={form.score} onChange={e=>setForm(f=>({...f,score:e.target.value}))} min="0" max="100" placeholder="e.g. 92"/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Start Date
                <input type="date" style={{ ...INP, marginTop:4 }} value={form.startDate} onChange={e=>setForm(f=>({...f,startDate:e.target.value}))}/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>End Date
                <input type="date" style={{ ...INP, marginTop:4 }} value={form.endDate} onChange={e=>setForm(f=>({...f,endDate:e.target.value}))}/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Certificate Expiry
                <input type="date" style={{ ...INP, marginTop:4 }} value={form.expiryDate} onChange={e=>setForm(f=>({...f,expiryDate:e.target.value}))}/>
              </label>
              <label style={{ gridColumn:'1/-1', fontSize:11, fontWeight:700, color:'#64748b' }}>Certificate URL
                <input style={{ ...INP, marginTop:4 }} value={form.certificateUrl} onChange={e=>setForm(f=>({...f,certificateUrl:e.target.value}))} placeholder="https://..."/>
              </label>
              <label style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={form.isCertified} onChange={e=>setForm(f=>({...f,isCertified:e.target.checked}))} style={{ width:16, height:16 }}/>
                <span style={{ fontSize:13, fontWeight:600 }}>🏆 Certified / Completed</span>
              </label>
              <label style={{ gridColumn:'1/-1', fontSize:11, fontWeight:700, color:'#64748b' }}>Notes
                <textarea style={{ ...INP, marginTop:4, minHeight:70, resize:'vertical' }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
              </label>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button onClick={()=>setModal(null)} style={{ padding:'9px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} style={{ padding:'9px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:700, cursor:'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
